/**
 * Abschnittsleiste (Board „Preisautomatik · Einstellungen“): springt zu einem der fünf immer offenen
 * Abschnitte und zeigt je Abschnitt, ob dort etwas geändert wurde oder ein Fehler steht.
 */
import { cn } from '@/shared/lib/cn';
import {
  SECTION_KEYS,
  SECTION_SHORT,
  sectionAnchor,
  sectionNumber,
  type SectionKey,
  type SectionMarker,
} from './configurationSections';

/** Springt zum Abschnitt und setzt den Fokus auf seinen Titel, damit Tastatur und Vorleser folgen. */
export function goToSection(key: SectionKey) {
  const target = document.getElementById(sectionAnchor(key));
  if (!target) return;
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView?.({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  target.querySelector<HTMLElement>('h2')?.focus({ preventScroll: true });
}

export const MARKER_TEXT: Record<Exclude<SectionMarker, null>, string> = {
  error: 'Fehler',
  changed: 'geändert',
};

export function ConfigurationSectionNavigation({
  markers,
}: {
  markers: Record<SectionKey, SectionMarker>;
}) {
  return (
    <nav
      aria-label="Abschnitte"
      className="bg-card rounded-tile shadow-float scrollbar-none @5xl:sticky @5xl:top-4 @5xl:flex-col @5xl:overflow-visible flex gap-0.5 overflow-x-auto p-2.5"
    >
      {SECTION_KEYS.map((key) => {
        const marker = markers[key];
        return (
          <button
            key={key}
            type="button"
            onClick={() => goToSection(key)}
            className="rounded-control text-ink hover:bg-card-alt focus-visible:outline-forest flex min-h-11 flex-none items-center gap-2.5 px-2.5 text-left text-[13px] font-semibold focus-visible:outline focus-visible:outline-2"
          >
            <span className="bg-card-alt grid size-6 flex-none place-items-center rounded-full text-xs font-extrabold">
              {sectionNumber(key)}
            </span>
            <span className="grow whitespace-nowrap">{SECTION_SHORT[key]}</span>
            {marker && (
              <span
                className={cn(
                  'size-[9px] flex-none rounded-full',
                  marker === 'error' ? 'bg-sealwax' : 'bg-amber',
                )}
              >
                <span className="sr-only">{MARKER_TEXT[marker]}</span>
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
